import { forwardRef } from 'react';

import { TextField, type TextFieldProps } from '../TextField';

export interface NumberInputProps extends Omit<TextFieldProps<'outlined'>, 'variant'> {
  allowNegative?: boolean;
  maxDecimalPlaces?: number;
  min?: number;
  max?: number;
}

function NumberInput(props: Readonly<NumberInputProps>, ref: React.Ref<HTMLDivElement>) {
  const {
    allowNegative: isNegativeAllowed = false,
    onChange,
    value,
    maxDecimalPlaces = 0,
    min,
    max,
    ...restProps
  } = props;

  const clamp = (value: number, min?: number, max?: number) => {
    return Math.min(max ?? value, Math.max(min ?? value, value));
  };

  const emitChange = (value: string | number | null) => {
    onChange?.({
      target: { value },
    } as React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>);
  };

  const handleNegativeValue = (value: string) => {
    if (value.startsWith('-')) {
      const minusCount = (value.match(/-/g) || []).length;

      if (!isNegativeAllowed) return true;
      if (value.length === 1) {
        emitChange(value);
        return true;
      }
      return minusCount > 1;
    }
    return false;
  };

  const handleDecimalPoint = (value: string) => {
    if (value.startsWith('.')) {
      return true;
    }

    const isEndWithDot = value.endsWith('.');
    const dotCount = value.split('.').length - 1;
    const decimalPlaces = value.split('.')[1]?.length ?? 0;

    if (isEndWithDot) {
      if (dotCount === 1 && maxDecimalPlaces > 0) {
        emitChange(value);
        return true;
      }
      return true;
    }

    if (dotCount === 1 && value.endsWith('0') && decimalPlaces <= maxDecimalPlaces) {
      emitChange(value);
      return true;
    }

    return false;
  };

  const processNumericValue = (value: string) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return;

    const decimalPlaces = value.split('.')[1]?.length ?? 0;
    if (decimalPlaces <= maxDecimalPlaces) {
      emitChange(clamp(numericValue, min, max));
    }
  };

  const handleOnValueChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const value = event.target.value;

    if (value === '') {
      emitChange(value);
      return;
    }

    if (handleNegativeValue(value)) {
      return;
    }

    if (handleDecimalPoint(value)) {
      return;
    }

    processNumericValue(value);
  };

  const handleBlur = () => {
    if (typeof value === 'string' && (value.endsWith('0') || value.endsWith('.'))) {
      emitChange(parseFloat(value));
    }
  };

  return (
    <TextField
      {...restProps}
      ref={ref}
      value={value ?? ''}
      onChange={handleOnValueChange}
      onBlur={handleBlur}
    />
  );
}

export default forwardRef(NumberInput);
